import React from 'react'

import cn from 'classnames'

import { FadeIn, Spinner, Label, OrderListItem } from '@pb/components'
import { getLabelFromStorage } from '@pb/utils'

import { ACCOUNT_ROUTES } from 'App/constants'
import optimizelyConstants from 'constants/optimizely'
import useOptimizelyDecision from 'features/account/hooks/useOptimizelyDecision'
import SecondaryAction from 'ui/buttons/SecondaryAction'

import { NormalizedOnlineOrder } from '../../duck/types'
import { sortByDate } from '../../helpers/purchases'
import OrderItem from './components/OrderItem'
import OrderItemNS from './components/OrderItemNS'
import styles from './OnlineOrdersList.scss'

export interface OnlineOrderListProps {
  isOrdersLoading: boolean
  orders: NormalizedOnlineOrder
  shouldShowLoadMoreButton: boolean
  loadMoreOrders: () => void
}

const OnlineOrdersList: React.FC<any> = ({
  loadMoreOrders,
  isOrdersLoading,
  orders,
  shouldShowLoadMoreButton,
  onOrderItemClick,
}) => {
  const isNorthstarBrand = useOptimizelyDecision(optimizelyConstants.ACCOUNT_NORTHSTAR_VARIATION)

  const nonArchivedOrders = orders.filter((order) => !order.isArchivedOrder)
  const archivedOrders = orders.filter((order) => order.isArchivedOrder).sort(sortByDate)
  const shouldShowArchivedOrders = !!archivedOrders.length
  const RenderComponent = isNorthstarBrand ? OrderItemNS : OrderItem

  return (
    <div className={styles.container}>
      <ul className={styles.onlineOrdersList}>
        {nonArchivedOrders.map(
          (
            { orderId, date, totalAmount, statusDisplay, isCancelled, itemNumber, productImages },
            index,
          ) => (
            <RenderComponent
              key={orderId}
              orderRoute={`${ACCOUNT_ROUTES.OnlineOrderDetails.path}/${orderId}`}
              orderMetaData={getLabelFromStorage('commerce_filter_itemsText', {
                replacements: itemNumber,
              })}
              orderDate={date}
              orderAmount={totalAmount}
              orderStatus={statusDisplay}
              orderImages={productImages}
              isOrderCancelled={isCancelled}
              onClick={() => onOrderItemClick(index)}
            />
          ),
        )}
      </ul>
      {shouldShowArchivedOrders && (
        <ul className={styles.archivedOrders} data-testid="archived-orders">
          {archivedOrders.map(({ orderId, date }) => (
            <OrderListItem
              Tag="li"
              isArchivedOrder
              key={orderId}
              orderStatusDisplay={date}
              orderDetailRoute={`${ACCOUNT_ROUTES.OnlineOrderDetails.path}/${orderId}`}
            />
          ))}
        </ul>
      )}

      {shouldShowLoadMoreButton && (
        <FadeIn
          className={cn(
            styles.loadMoreButtonContainer,
            isNorthstarBrand && styles.hidePadding_bottom,
          )}
          delay={75 * (orders.length + 1)}
        >
          <SecondaryAction
            size="large"
            fullWidth
            onClick={loadMoreOrders}
            className={styles.loadMoreButton}
            disabled={isOrdersLoading}
            data-testid="load-more-orders"
          >
            <Spinner isRendered={isOrdersLoading} containerClassName={styles.spinner} />
            <Label copyKey="account.digitalreceipts.loadmore" />
          </SecondaryAction>
        </FadeIn>
      )}
    </div>
  )
}

export default OnlineOrdersList
